import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Menu, X, ClipboardCheck, Users, User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const menuItems = [
    { id: 'assessment', label: 'Assessment', icon: ClipboardCheck, path: '/dashboard' },
    { id: 'candidates', label: 'Candidates', icon: Users, path: '/dashboard/candidates' },
  ];

  // Determine active menu item based on current route
  const getActiveMenuItem = () => {
    if (location.pathname.startsWith('/dashboard/assessment/')) {
      return 'assessment';
    }
    if (location.pathname.startsWith('/dashboard/candidates')) {
      return 'candidates';
    }
    return 'assessment'; // default
  };

  const activeMenuItem = getActiveMenuItem();

  const handleMenuClick = (path: string, itemId: string) => {
    navigate(path);
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Overlay for mobile */}
      {isMobile && isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={toggleDrawer}
        />
      )}

      {/* Drawer */}
      <aside
        className={`
          fixed lg:static
          top-0 left-0
          h-full
          bg-white
          shadow-lg
          z-50
          transition-transform duration-300 ease-in-out
          ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMobile ? 'w-64' : 'w-64'}
        `}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <img 
              src="https://portal.earlyjobs.ai/logo512.png" 
              alt="Logo" 
              className="h-8 w-8"
            />
            <h2 className="text-lg font-semibold text-gray-800">EarlyJobs</h2>
          </div>
          {isMobile && (
            <button
              onClick={toggleDrawer}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Drawer Menu */}
        <nav className="p-4 space-y-2 overflow-y-auto h-[calc(100vh-80px)]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenuItem === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.path, item.id)}
                className={`
                  w-full
                  flex items-center space-x-3
                  px-4 py-3
                  rounded-lg
                  text-left
                  transition-all duration-200
                  font-medium
                  ${
                    isActive
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDrawer}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors lg:hidden"
                aria-label="Toggle drawer"
              >
                <Menu className="h-6 w-6 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-gray-800">
                {activeMenuItem === 'candidates' ? 'Candidates' : 'Assessment'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* User Avatar */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="User menu"
                >
                  {user?.profile?.firstName ? (
                    <span className="text-sm font-semibold">
                      {user.profile.firstName.charAt(0).toUpperCase()}
                      {user.profile.lastName?.charAt(0).toUpperCase() || ''}
                    </span>
                  ) : user?.email ? (
                    <span className="text-sm font-semibold">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                </button>

                {/* User Menu Modal */}
                {isUserMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                          {user?.profile?.firstName && user?.profile?.lastName
                            ? `${user.profile.firstName} ${user.profile.lastName}`
                            : user?.email || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate('/dashboard/profile');
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Settings</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area - This is where child routes will render */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
